import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { renderMarkdown } from './markdown';
import { normalizeSearchText, parseSearchQuery, searchableText, matchesSearch } from './search';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { useNavigate } from 'react-router-dom';
import './CypherApp.css';
import { CYPHER_API_BASE } from '../apiConfig';

const API_BASE = CYPHER_API_BASE;

function itemFromUrl() {
  return new URLSearchParams(window.location.search).get('item');
}

async function api(path, options) {
  const response = await fetch(API_BASE + path, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || 'Could not save this change.');
  return body;
}

function MarkdownContent({ content, onClick }) {
  const rendered = useMemo(() => renderMarkdown(content), [content]);
  return <div className="cy-prose" onClick={onClick} dangerouslySetInnerHTML={{ __html: rendered }} />;
}

const markdownEditorComponents = {
  preview: source => <MarkdownContent content={source} />
};

export default function CypherApp() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(itemFromUrl);
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [fullText, setFullText] = useState(false);
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState({ name: '', category: '', content: '' });
  const categorySyncedId = useRef(null);
  const resultListRef = useRef(null);
  const [newCategory, setNewCategory] = useState('');
  const [dropCategory, setDropCategory] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let active = true;
    api('/state')
      .then(state => {
        if (!active) return;
        setEntries(state.entries);
        setCategories(state.categories);
        setLoading(false);
      })
      .catch(reason => {
        if (!active) return;
        setError(reason.message + ' Start the Cypher API and reload this page.');
        setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onPop = () => setSelectedId(itemFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const byId = useMemo(() => new Map(entries.map(item => [item.id, item])), [entries]);
  const selected = selectedId ? byId.get(selectedId) : null;
  const visibleItems = useMemo(() => entries.filter(item => !item.hidden), [entries]);
  const searchIndex = useMemo(
    () => new Map(visibleItems.map(item => [item.id, searchableText(item, true)])),
    [visibleItems]
  );
  const counts = useMemo(() => {
    const result = new Map(categories.map(name => [name, 0]));
    visibleItems.forEach(item => result.set(item.category, (result.get(item.category) || 0) + 1));
    return result;
  }, [categories, visibleItems]);

  useEffect(() => {
    if (!selectedId) {
      categorySyncedId.current = null;
    } else if (selected && itemFromUrl() === selectedId && categorySyncedId.current !== selectedId) {
      categorySyncedId.current = selectedId;
      setCategory(selected.category);
    }
  }, [selected, selectedId]);

  const results = useMemo(() => {
    const terms = parseSearchQuery(query);
    const exactName = terms.map(term => term.text).join(' ');
    return visibleItems.filter(item => {
      if (category !== 'All' && item.category !== category) return false;
      if (!terms.length) return true;
      const haystack = fullText ? searchIndex.get(item.id) : searchableText(item);
      return matchesSearch(haystack, terms);
    }).sort((a, b) => {
      if (terms.length) {
        const aExact = normalizeSearchText(a.name) === exactName;
        const bExact = normalizeSearchText(b.name) === exactName;
        if (aExact !== bExact) return aExact ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [visibleItems, category, query, fullText, searchIndex]);

  const selectItem = useCallback((id, { preserveCategory = false } = {}) => {
    if (preserveCategory) categorySyncedId.current = id;
    setSelectedId(id);
    setEditor(null);
    setMobileOpen(false);
    setError('');
    navigate('/cypher?item=' + encodeURIComponent(id));
  }, [navigate]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (editor || !results.length || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const field = event.target.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"]');
      if (field && field.id !== 'cy-search') return;

      event.preventDefault();
      const index = results.findIndex(item => item.id === selectedId);
      const nextIndex = index < 0
        ? (event.key === 'ArrowDown' ? 0 : results.length - 1)
        : Math.max(0, Math.min(results.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
      if (results[nextIndex].id !== selectedId) {
        selectItem(results[nextIndex].id, { preserveCategory: true });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editor, results, selectedId, selectItem]);

  useEffect(() => {
    const list = resultListRef.current;
    const button = list?.querySelector('.cy-result.selected');
    if (!button) return;
    if (list.contains(document.activeElement)) button.focus({ preventScroll: true });
    button.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selectedId, results]);

  function followReferenceLink(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    let id;
    try { id = decodeURIComponent(link.getAttribute('href').slice(1)); } catch { return; }
    if (byId.has(id)) {
      event.preventDefault();
      selectItem(id);
    }
  }

  function startNew() {
    if (!categories.length) return;
    setDraft({ name: '', category: category === 'All' ? categories[0] : category, content: '' });
    setEditor('new');
    setSelectedId(null);
    setMobileOpen(false);
    setError('');
    navigate('/cypher');
  }

  function startEdit() {
    if (!selected) return;
    setDraft({
      name: selected.name,
      category: selected.category,
      content: selected.content || ''
    });
    setEditor('entry');
    setError('');
  }

  async function saveEntry(event) {
    event.preventDefault();
    if (!draft.name.trim()) return;
    const payload = {
      name: draft.name.trim(),
      category: draft.category,
      content: draft.content
    };
    try {
      const item = await api(editor === 'new' ? '/entries' : '/entries/' + encodeURIComponent(selected.id), {
        method: editor === 'new' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setEntries(current => editor === 'new' ? [...current, item] : current.map(entry => entry.id === item.id ? item : entry));
      setCategory(item.category);
      setEditor(null);
      setSelectedId(item.id);
      setError('');
      navigate('/cypher?item=' + encodeURIComponent(item.id));
    } catch (reason) {
      setError(reason.message);
    }
  }

  async function deleteEntry() {
    if (!selected || !window.confirm('Delete this entry for everyone using the Cypher library?')) return;
    try {
      await api('/entries/' + encodeURIComponent(selected.id), { method: 'DELETE' });
      setEntries(current => current.filter(item => item.id !== selected.id));
      setSelectedId(null);
      setEditor(null);
      setError('');
      navigate('/cypher');
    } catch (reason) {
      setError(reason.message);
    }
  }

  async function createCategory(event) {
    event.preventDefault();
    const name = newCategory.trim();
    if (!name) return;
    try {
      const created = await api('/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      setCategories(current => [...current, created.name]);
      setCategory(created.name);
      setNewCategory('');
      setError('');
    } catch (reason) {
      setError(reason.message);
    }
  }

  async function moveEntry(id, targetCategory) {
    const item = byId.get(id);
    if (!item || item.category === targetCategory) return;
    try {
      const updated = await api('/entries/' + encodeURIComponent(item.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: item.name,
          category: targetCategory,
          content: item.content || ''
        })
      });
      setEntries(current => current.map(entry => entry.id === updated.id ? updated : entry));
      setError('');
    } catch (reason) {
      setError(reason.message);
    }
  }

  return (
    <div className="cy-app">
      <button type="button" className="cy-menu-button" onClick={() => setMobileOpen(value => !value)} aria-controls="cy-sidebar" aria-expanded={mobileOpen}>Library</button>
      <div className="cy-shell">
        <aside id="cy-sidebar" className={'cy-sidebar' + (mobileOpen ? ' cy-open' : '')}>
          <div className="cy-sidebar-head">
            <p>{loading ? 'Loading reference…' : visibleItems.length.toLocaleString() + ' entries'}</p>
          </div>
          <div className="cy-search-wrap">
            <label htmlFor="cy-search">Search entries</label>
            <input id="cy-search" type="search" aria-describedby="cy-search-help" placeholder="Find an ability, Type, or rule…" value={query} onChange={event => setQuery(event.target.value)} />
            <p id="cy-search-help" className="cy-search-help">Use "quotes" for an exact phrase.</p>
            <label className="cy-check"><input type="checkbox" checked={fullText} onChange={event => setFullText(event.target.checked)} /> Search full text</label>
          </div>
          <div className="cy-category-head"><span>Categories</span><button type="button" onClick={startNew}>+ New entry</button></div>
          <nav className="cy-categories" aria-label="Cypher categories">
            <button type="button" className={category === 'All' ? 'active' : ''} onClick={() => setCategory('All')}>All entries <span>{visibleItems.length.toLocaleString()}</span></button>
            {categories.map(name => <button
              type="button"
              key={name}
              className={(category === name ? 'active ' : '') + (dropCategory === name ? 'cy-drop-target' : '')}
              onClick={() => setCategory(name)}
              onDragOver={event => { event.preventDefault(); setDropCategory(name); }}
              onDragLeave={() => setDropCategory('')}
              onDrop={event => { event.preventDefault(); setDropCategory(''); moveEntry(event.dataTransfer.getData('text/plain'), name); }}
            >{name}<span>{(counts.get(name) || 0).toLocaleString()}</span></button>)}
          </nav>
          <form className="cy-new-category" onSubmit={createCategory}>
            <label htmlFor="cy-new-category">New category</label>
            <div><input id="cy-new-category" value={newCategory} onChange={event => setNewCategory(event.target.value)} placeholder="Category name" /><button type="submit">Add</button></div>
          </form>
          <p className="cy-drag-hint">Drag an entry onto a category to move it.</p>
        </aside>
        <main className="cy-main">
          <div className="cy-results">
            <div className="cy-results-head"><span>{category}</span><strong>{results.length.toLocaleString()} entries</strong></div>
            <div className="cy-result-list" ref={resultListRef}>
              {results.map(item => <button
                type="button"
                draggable
                onDragStart={event => event.dataTransfer.setData('text/plain', item.id)}
                key={item.id}
                className={'cy-result' + (item.id === selected?.id ? ' selected' : '')}
                aria-current={item.id === selected?.id ? 'true' : undefined}
                onClick={() => selectItem(item.id)}
              ><span>{item.name}</span></button>)}
              {!results.length && !loading && <div className="cy-empty-results">No matching entries.</div>}
            </div>
          </div>
          <article className="cy-content">
            {error && <div className="cy-error" role="alert">{error}</div>}
            {editor === 'new' || editor === 'entry' ? (
              <form className="cy-form" onSubmit={saveEntry}>
                <div className="cy-title-row cy-edit-title-row">
                  <h1>{editor === 'new' ? 'New entry' : 'Edit entry'}</h1>
                  <div className="cy-form-actions"><button type="submit">Save entry</button><button type="button" onClick={() => setEditor(null)}>Cancel</button></div>
                </div>
                <label>Name<input required value={draft.name} onChange={event => setDraft(value => ({ ...value, name: event.target.value }))} /></label>
                <label>Category<select value={draft.category} onChange={event => setDraft(value => ({ ...value, category: event.target.value }))}>{categories.map(name => <option key={name}>{name}</option>)}</select></label>
                <div className="cy-body-editor" data-color-mode="light">
                  <label htmlFor="cy-markdown-body">Page content</label>
                  <p className="cy-editor-hint">Use Markdown for headings, lists, links, and tables. Preview shows the page formatting.</p>
                  <MDEditor
                    value={draft.content}
                    onChange={content => setDraft(value => ({ ...value, content: content || '' }))}
                    height={420}
                    preview="edit"
                    textareaProps={{ id: 'cy-markdown-body', 'aria-label': 'Page content' }}
                    components={markdownEditorComponents}
                  />
                </div>
              </form>
            ) : selected ? (
              <>
                <div className="cy-title-row"><div><h1>{selected.name}</h1><span className="cy-pill">{selected.category}</span></div><button type="button" onClick={startEdit}>Edit entry</button></div>
                <MarkdownContent content={selected.content || '*No content yet.*'} onClick={followReferenceLink} />
                <div className="cy-entry-actions"><button type="button" className="cy-delete" onClick={deleteEntry}>Delete entry</button></div>
              </>
            ) : !loading && selectedId ? <p role="status">Entry not found.</p> : null}
          </article>
        </main>
      </div>
    </div>
  );
}
